import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Link as MuiLink,
  Paper,
  Snackbar,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router';

import {
  adminCatalogApi,
  CatalogRow,
  CatalogSlug,
} from '../../../api/adminCatalogApi';
import { paths } from '../../../routes/paths';
import {
  colors,
  primaryButtonSx,
  tableCellSx,
  tableHeadCellSx,
  typo,
} from '../../../theme/tokens';
import { WriteButton, WriteIconButton } from '../../../components/ui';
import { useWriteGuard } from '../../../context/useWriteGuard';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

/**
 * An optional per-catalog inline-editable scalar column. Rendered after
 * the `description` cell in both existing rows and the add-new row. For
 * `'text'` we use a small TextField; for `'select'` we use MUI's native
 * select. Inline edits commit on blur / Enter, matching the name column.
 *
 * Round 6 wires this for canonical-fields (`data_type`) and
 * cohort-attribute-defs (`data_type` + `value_source`). Extending a
 * catalog is a one-liner on the wrapper page — no shell edit required.
 */
export interface CatalogShellExtraColumn {
  name: 'data_type' | 'value_source';
  label: string;
  type: 'text' | 'select';
  options?: string[];
  width?: number;
}

export interface CatalogShellProps {
  slug: CatalogSlug;
  title: string;
  emptyBody: string;
  addCtaLabel: string;
  /** One-line breadcrumb context. */
  breadcrumbLabel: string;
  /** Optional scalar columns for catalogs that need additional fields. */
  extraColumns?: CatalogShellExtraColumn[];
}

type EditingCell = {
  code: string;
  field: 'name' | 'description' | 'data_type' | 'value_source';
  value: string;
} | null;

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

function formatRelative(iso: string | null | undefined): string {
  if (!iso) return '—';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '—';
  const diffSec = Math.round((Date.now() - then) / 1000);
  if (diffSec < 60) return 'just now';
  const min = Math.round(diffSec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.round(hr / 24);
  if (day < 30) return `${day}d ago`;
  const mo = Math.round(day / 30);
  return `${mo}mo ago`;
}

/* ------------------------------------------------------------------ */
/*  Shell                                                              */
/* ------------------------------------------------------------------ */

const CatalogShell: React.FC<CatalogShellProps> = ({
  slug,
  title,
  emptyBody,
  addCtaLabel,
  breadcrumbLabel,
  extraColumns = [],
}) => {
  const navigate = useNavigate();
  const { readOnly } = useWriteGuard();
  const queryClient = useQueryClient();
  const queryKey = useMemo(() => ['admin', 'catalogs', slug], [slug]);

  const listQuery = useQuery<CatalogRow[]>({
    queryKey,
    queryFn: () => adminCatalogApi.list(slug),
  });

  const [editing, setEditing] = useState<EditingCell>(null);
  const [newForm, setNewForm] = useState<{
    code: string;
    name: string;
    description: string;
    /** Extra-column values keyed by name (data_type, value_source). */
    extra: Record<string, string>;
  }>({
    code: '',
    name: '',
    description: '',
    extra: {},
  });
  const [showAdd, setShowAdd] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    msg: string;
    sev: 'success' | 'error' | 'info';
  }>({ open: false, msg: '', sev: 'success' });

  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (editing && inputRef.current) inputRef.current.focus();
  }, [editing?.code, editing?.field]); // eslint-disable-line react-hooks/exhaustive-deps

  const rows = listQuery.data ?? [];

  /* ------------------ Mutations ------------------ */

  const updateMutation = useMutation({
    mutationFn: async (args: {
      code: string;
      patch: {
        name?: string;
        description?: string | null;
        active?: boolean;
        data_type?: string | null;
        value_source?: string | null;
      };
    }) => adminCatalogApi.update(slug, args.code, args.patch),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      setEditing(null);
    },
    onError: () => {
      setSnackbar({ open: true, msg: 'Update failed', sev: 'error' });
      setEditing(null);
    },
  });

  const createMutation = useMutation({
    mutationFn: async () => {
      // Cast through the shared adminCatalogApi create signature; extra
      // scalar fields (data_type, value_source) ride along via the row
      // PATCH right after create. This keeps the create surface of
      // adminCatalogApi tight while still supporting initial values
      // entered in the add-new row.
      const created = await adminCatalogApi.create(slug, {
        code: newForm.code.trim(),
        name: newForm.name.trim(),
        description: newForm.description.trim() || null,
      });
      // If any extra columns carried a value, apply them in a follow-up
      // PATCH so the newly-created row shows the right data on reload.
      const extraPatch: Record<string, string | null> = {};
      for (const col of extraColumns) {
        const v = newForm.extra[col.name]?.trim();
        if (v) extraPatch[col.name] = v;
      }
      if (Object.keys(extraPatch).length > 0) {
        await adminCatalogApi.update(slug, created.code, extraPatch);
      }
      return created;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      setNewForm({ code: '', name: '', description: '', extra: {} });
      setShowAdd(false);
      setSnackbar({ open: true, msg: 'Row added', sev: 'success' });
    },
    onError: (err: any) => {
      const code = err?.response?.data?.error;
      if (code === 'conflict') {
        setSnackbar({ open: true, msg: 'Code already exists', sev: 'error' });
      } else {
        setSnackbar({ open: true, msg: 'Create failed', sev: 'error' });
      }
    },
  });

  const commitEdit = useCallback(() => {
    if (!editing) return;
    const current = rows.find((r) => r.code === editing.code);
    if (!current) {
      setEditing(null);
      return;
    }
    const originalVal: string =
      editing.field === 'name'
        ? current.name
        : editing.field === 'description'
          ? current.description ?? ''
          : ((current as any)[editing.field] ?? '') + '';
    if (editing.value === originalVal) {
      setEditing(null);
      return;
    }
    const trimmed = editing.value.trim();
    const patch: Record<string, unknown> =
      editing.field === 'name'
        ? { name: trimmed }
        : editing.field === 'description'
          ? { description: trimmed || null }
          : { [editing.field]: trimmed || null };
    updateMutation.mutate({ code: editing.code, patch });
  }, [editing, rows, updateMutation]);

  const startEdit = (
    code: string,
    field: 'name' | 'description' | 'data_type' | 'value_source',
    value: string,
  ) => {
    setEditing({ code, field, value });
  };

  /* ------------------ Render ------------------ */

  const showEmpty = !listQuery.isLoading && rows.length === 0;

  return (
    <Box sx={{ maxWidth: 1120 }}>
      {/* Breadcrumb + Title + Count */}
      <Typography
        sx={{
          fontSize: 12,
          fontWeight: 600,
          color: colors.textSecondary,
          textTransform: 'uppercase',
          letterSpacing: '0.6px',
          mb: 1,
        }}
      >
        <MuiLink
          onClick={() => navigate(paths.admin.home())}
          sx={{ cursor: 'pointer', color: 'inherit', textDecoration: 'none', mr: 0.5 }}
        >
          Admin
        </MuiLink>
        /
        <MuiLink
          onClick={() => navigate(paths.admin.catalogs.root())}
          sx={{ cursor: 'pointer', color: 'inherit', textDecoration: 'none', mx: 0.5 }}
        >
          Reference Catalogs
        </MuiLink>
        /
        <span style={{ marginLeft: 4, color: colors.textPrimary }}>{breadcrumbLabel}</span>
      </Typography>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          mb: 3,
          flexWrap: 'wrap',
        }}
      >
        <Box>
          <Typography sx={{ ...typo.pageTitle, mb: 0.5 }}>{title}</Typography>
          <Typography sx={{ ...typo.pageSubtitle }}>
            {rows.length} {rows.length === 1 ? 'row' : 'rows'}
          </Typography>
        </Box>
        {!showAdd && !showEmpty && (
          <WriteButton
            startIcon={<AddIcon />}
            variant="contained"
            onClick={() => setShowAdd(true)}
            sx={primaryButtonSx}
          >
            Add row
          </WriteButton>
        )}
      </Box>

      {listQuery.isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress sx={{ color: colors.adminAccent }} />
        </Box>
      ) : showEmpty && !showAdd ? (
        <Paper
          elevation={0}
          sx={{
            borderRadius: '12px',
            border: `1px solid ${colors.borderLight}`,
            backgroundColor: colors.bgWhite,
            py: 8,
            px: 4,
            textAlign: 'center',
          }}
        >
          <Box
            sx={{
              width: 72,
              height: 72,
              borderRadius: '50%',
              backgroundColor: colors.adminAccentSubtle,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 2,
            }}
          >
            <AddIcon sx={{ fontSize: 36, color: colors.adminAccent }} />
          </Box>
          <Typography sx={{ ...typo.sectionTitle, fontSize: 18, mb: 1 }}>
            {title} is empty
          </Typography>
          <Typography sx={{ ...typo.secondaryText, mx: 'auto', maxWidth: 460, mb: 3 }}>
            {emptyBody}
          </Typography>
          <WriteButton
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setShowAdd(true)}
            sx={primaryButtonSx}
          >
            {addCtaLabel}
          </WriteButton>
        </Paper>
      ) : (
        <Paper
          elevation={0}
          sx={{
            borderRadius: '12px',
            border: `1px solid ${colors.borderLight}`,
            overflow: 'hidden',
          }}
        >
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ ...tableHeadCellSx, width: 220 }}>Code</TableCell>
                  <TableCell sx={tableHeadCellSx}>Name</TableCell>
                  <TableCell sx={tableHeadCellSx}>Description</TableCell>
                  {extraColumns.map((col) => (
                    <TableCell
                      key={col.name}
                      sx={{ ...tableHeadCellSx, width: col.width ?? 160 }}
                    >
                      {col.label}
                    </TableCell>
                  ))}
                  <TableCell sx={{ ...tableHeadCellSx, width: 96 }}>Active</TableCell>
                  <TableCell sx={{ ...tableHeadCellSx, width: 160 }}>Updated</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((r) => {
                  const isEditingName = editing?.code === r.code && editing.field === 'name';
                  const isEditingDesc =
                    editing?.code === r.code && editing.field === 'description';
                  return (
                    <TableRow key={r.code} hover>
                      <TableCell sx={{ ...tableCellSx, ...typo.mono, color: colors.textPrimary }}>
                        {r.code}
                      </TableCell>

                      {/* Name — inline editable */}
                      <TableCell
                        sx={{
                          ...tableCellSx,
                          cursor: isEditingName ? 'text' : 'pointer',
                        }}
                        onDoubleClick={() => !readOnly && startEdit(r.code, 'name', r.name)}
                        data-write-action="true"
                      >
                        {isEditingName ? (
                          <TextField
                            size="small"
                            value={editing.value}
                            inputRef={inputRef}
                            onChange={(e) =>
                              setEditing({ ...editing, value: e.target.value })
                            }
                            onBlur={commitEdit}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') commitEdit();
                              if (e.key === 'Escape') setEditing(null);
                            }}
                            inputProps={{ maxLength: 100 }}
                          />
                        ) : (
                          <Typography sx={{ fontSize: 13 }}>{r.name}</Typography>
                        )}
                      </TableCell>

                      {/* Description — inline editable */}
                      <TableCell
                        sx={{
                          ...tableCellSx,
                          cursor: isEditingDesc ? 'text' : 'pointer',
                          color: colors.textSecondary,
                        }}
                        onDoubleClick={() =>
                          !readOnly && startEdit(r.code, 'description', r.description ?? '')
                        }
                        data-write-action="true"
                      >
                        {isEditingDesc ? (
                          <TextField
                            size="small"
                            fullWidth
                            value={editing.value}
                            inputRef={inputRef}
                            onChange={(e) =>
                              setEditing({ ...editing, value: e.target.value })
                            }
                            onBlur={commitEdit}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') commitEdit();
                              if (e.key === 'Escape') setEditing(null);
                            }}
                          />
                        ) : (
                          <Typography sx={{ fontSize: 13, color: colors.textSecondary }}>
                            {r.description || (
                              <span style={{ color: colors.textTertiary, fontStyle: 'italic' }}>
                                Double-click to add…
                              </span>
                            )}
                          </Typography>
                        )}
                      </TableCell>

                      {/* Extra inline-editable scalar cells (e.g. data_type, value_source) */}
                      {extraColumns.map((col) => {
                        const currentVal = ((r as any)[col.name] ?? '') + '';
                        const isEditingExtra =
                          editing?.code === r.code && editing.field === col.name;
                        return (
                          <TableCell
                            key={col.name}
                            sx={{
                              ...tableCellSx,
                              cursor: isEditingExtra ? 'text' : 'pointer',
                              color: colors.textSecondary,
                            }}
                            onDoubleClick={() => !readOnly && startEdit(r.code, col.name, currentVal)}
                            data-write-action="true"
                          >
                            {isEditingExtra ? (
                              col.type === 'select' ? (
                                <TextField
                                  size="small"
                                  select
                                  SelectProps={{ native: true }}
                                  value={editing.value}
                                  inputRef={inputRef}
                                  onChange={(e) =>
                                    setEditing({ ...editing, value: e.target.value })
                                  }
                                  onBlur={commitEdit}
                                  autoFocus
                                >
                                  <option value=""></option>
                                  {(col.options ?? []).map((o) => (
                                    <option key={o} value={o}>
                                      {o}
                                    </option>
                                  ))}
                                </TextField>
                              ) : (
                                <TextField
                                  size="small"
                                  value={editing.value}
                                  inputRef={inputRef}
                                  onChange={(e) =>
                                    setEditing({ ...editing, value: e.target.value })
                                  }
                                  onBlur={commitEdit}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') commitEdit();
                                    if (e.key === 'Escape') setEditing(null);
                                  }}
                                  inputProps={{ maxLength: 100 }}
                                />
                              )
                            ) : currentVal ? (
                              <Typography
                                sx={{
                                  fontSize: 12,
                                  fontFamily: 'Fira Code, monospace',
                                  color: colors.textSecondary,
                                }}
                              >
                                {currentVal}
                              </Typography>
                            ) : (
                              <Typography
                                sx={{ fontSize: 12, color: colors.textTertiary, fontStyle: 'italic' }}
                              >
                                Double-click to set…
                              </Typography>
                            )}
                          </TableCell>
                        );
                      })}

                      {/* Active switch */}
                      <TableCell sx={tableCellSx}>
                        <Switch
                          size="small"
                          checked={r.active}
                          onChange={(e) =>
                            updateMutation.mutate({
                              code: r.code,
                              patch: { active: e.target.checked },
                            })
                          }
                          disabled={updateMutation.isPending || readOnly}
                        />
                      </TableCell>

                      <TableCell sx={{ ...tableCellSx, color: colors.textSecondary }}>
                        {formatRelative(r.updated_at ?? r.created_at)}
                      </TableCell>
                    </TableRow>
                  );
                })}

                {/* Add-new row */}
                {showAdd && (
                  <TableRow sx={{ backgroundColor: colors.adminAccentSubtle }}>
                    <TableCell sx={{ ...tableCellSx }}>
                      <TextField
                        size="small"
                        placeholder="new-code"
                        value={newForm.code}
                        onChange={(e) =>
                          setNewForm({ ...newForm, code: e.target.value })
                        }
                        inputProps={{ maxLength: 50 }}
                        InputProps={{
                          sx: { fontFamily: 'Fira Code, monospace', fontSize: 12 },
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ ...tableCellSx }}>
                      <TextField
                        size="small"
                        placeholder="Display name"
                        value={newForm.name}
                        onChange={(e) =>
                          setNewForm({ ...newForm, name: e.target.value })
                        }
                        inputProps={{ maxLength: 100 }}
                      />
                    </TableCell>
                    <TableCell sx={{ ...tableCellSx }}>
                      <TextField
                        size="small"
                        fullWidth
                        placeholder="Optional description"
                        value={newForm.description}
                        onChange={(e) =>
                          setNewForm({ ...newForm, description: e.target.value })
                        }
                      />
                    </TableCell>
                    {extraColumns.map((col) => (
                      <TableCell key={col.name} sx={tableCellSx}>
                        {col.type === 'select' ? (
                          <TextField
                            size="small"
                            select
                            SelectProps={{ native: true }}
                            value={newForm.extra[col.name] ?? ''}
                            onChange={(e) =>
                              setNewForm({
                                ...newForm,
                                extra: { ...newForm.extra, [col.name]: e.target.value },
                              })
                            }
                          >
                            <option value=""></option>
                            {(col.options ?? []).map((o) => (
                              <option key={o} value={o}>
                                {o}
                              </option>
                            ))}
                          </TextField>
                        ) : (
                          <TextField
                            size="small"
                            placeholder={col.label}
                            value={newForm.extra[col.name] ?? ''}
                            onChange={(e) =>
                              setNewForm({
                                ...newForm,
                                extra: { ...newForm.extra, [col.name]: e.target.value },
                              })
                            }
                            inputProps={{ maxLength: 100 }}
                          />
                        )}
                      </TableCell>
                    ))}
                    <TableCell sx={tableCellSx}>—</TableCell>
                    <TableCell sx={tableCellSx}>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Tooltip title="Save (Enter)">
                          <span>
                            <WriteIconButton
                              size="small"
                              disabled={
                                !newForm.code.trim() ||
                                !newForm.name.trim() ||
                                createMutation.isPending
                              }
                              onClick={() => createMutation.mutate()}
                              sx={{ color: colors.successIcon }}
                            >
                              <CheckIcon fontSize="small" />
                            </WriteIconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Cancel (Esc)">
                          <IconButton
                            size="small"
                            onClick={() => {
                              setShowAdd(false);
                              setNewForm({ code: '', name: '', description: '', extra: {} });
                            }}
                            sx={{ color: colors.textSecondary }}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                )}

                {/* Add-trigger row (tenant-table muscle-memory: + at the bottom) */}
                {!showAdd && rows.length > 0 && (
                  <TableRow
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => setShowAdd(true)}
                    data-write-action="true"
                  >
                    <TableCell colSpan={5 + extraColumns.length} sx={{ ...tableCellSx, py: 1, textAlign: 'center' }}>
                      <Box
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 1,
                          color: colors.adminAccent,
                          fontWeight: 600,
                          fontSize: 13,
                        }}
                      >
                        <AddIcon fontSize="small" /> Add row
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {listQuery.isError && (
        <Alert severity="error" sx={{ mt: 2 }}>
          Failed to load.
        </Alert>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3500}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          severity={snackbar.sev}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          sx={{ borderRadius: '8px' }}
        >
          {snackbar.msg}
        </Alert>
      </Snackbar>

    </Box>
  );
};

export default CatalogShell;
